import type { Mutable } from 'utility-types'

import { areArrayEquals, Droppable, type Entries, executeOnce, objectEntries, Queue } from '../../../utils'

import {
    selectExplorer,
    selectExplorerCustomTopics,
    selectExplorerLatestTopicValues,
    selectExplorerSelectedTopics,
    selectLoadedExplorer,
} from '../../../domain'

import { ExplorerChunksUsecase, initialState, type TopicsSubscription } from '../..'
import { ExplorerUsecase, initialExplorerState } from './Explorer'

export class StartExplorerSubscriptionUsecase extends ExplorerUsecase {
    private createTopicSelectionRefresher (subscription: TopicsSubscription, droppable: Pick<Droppable, 'isDropped'>): VoidFunction {
        /**
         * The queue ensure each wait for the next
         */
        const queue = new Queue()

        return () => {
            void queue.push(async () => {
                if (droppable.isDropped) {
                    return
                }

                /** Reset if needed */
                if (selectLoadedExplorer(this.getState()).subscriptionResponse !== null) {
                    this.setExplorer({ subscriptionResponse: null })
                }

                const selectedTopics = selectExplorerSelectedTopics(this.getState())
                const subscriptionResponse = await subscription.subscribe(selectedTopics)

                /** Check if subscription has been dropped or if another set of topics have been selected */
                if (droppable.isDropped || selectedTopics !== selectExplorerSelectedTopics(this.getState())) {
                    return
                }

                const getIndexedSelectedTopics = executeOnce(() => new Set(selectedTopics))

                /**
                 * Recreate last values without topics that may be gone
                 */
                const latestValues: Mutable<Entries<ReturnType<typeof selectExplorerLatestTopicValues>>> = []

                /**
                 * Selected topics that are exact get to be pre-added as null entries
                 */
                for (const topic of selectedTopics) {
                    if (this.topicsService.createTopic(topic).exact) {
                        latestValues.push([topic, null])
                    }
                }

                /**
                 * Messages from topics that are still being subscribed to get to still be present
                 */
                for (const [topic, message] of objectEntries(selectExplorerLatestTopicValues(this.getState()))) {
                    if (message?.sources.some((s) => getIndexedSelectedTopics().has(s))) {
                        latestValues.push([topic, message])
                    }
                }

                this.setExplorer({ subscriptionResponse, latestValues: Object.fromEntries(latestValues) })
            })
        }
    }

    /**
     * Creates a topic subscription that will wait for topics to be subscribed to
     * @returns a function to drop the subscription
     */
    invoke (): VoidFunction {
        const droppable = new Droppable()

        /** Initialize the state of the explorer */
        this.setState({ explorer: initialExplorerState })

        /** Retrive from the persistence layer the topics config and listen to changes */
        const { selected: selectedTopics, custom: customTopics } = this.topicsPersistenceService.retrieveTopics()
        if (selectedTopics.length + customTopics.length) {
            this.setExplorer({ selectedTopics, customTopics })
        }
        droppable.onDrop(
            this.subscribeToState(
                (a, b) =>
                    areArrayEquals(selectExplorerSelectedTopics(a), selectExplorerSelectedTopics(b)) &&
                    areArrayEquals(selectExplorerCustomTopics(a), selectExplorerCustomTopics(b)),
                () =>
                    this.topicsPersistenceService.persistTopics({
                        selected: selectExplorerSelectedTopics(this.getState()),
                        custom: selectExplorerCustomTopics(this.getState()),
                    })
            )
        )

        /** Already load the root chunk */
        this.getUsecase(ExplorerChunksUsecase).load(null, droppable)

        /** Start listening to topic messages */
        const subscription = this.topicsService.createSubscription()
        droppable.onDrop(() => subscription.end())

        /** Listen for change in selection, and update subscription accordingly */
        const topicsSelectionChanged = this.createTopicSelectionRefresher(subscription, droppable)
        droppable.onDrop(
            this.subscribeToState((a, b) => areArrayEquals(selectExplorerSelectedTopics(a), selectExplorerSelectedTopics(b)), topicsSelectionChanged)
        )
        topicsSelectionChanged()

        /**
         * If there is general error, just thrown it all over the place,
         * an error implies that the client will be closed
         */
        droppable.onDrop(subscription.onError((error) => this.setState({ explorer: error })))

        droppable.onDrop(
            subscription.onBatch((batch) => {
                const explorer = selectLoadedExplorer(this.getState())
                let { latestValues, tailSize, tail } = explorer

                batch.forEach(({ timestamp, possibleTypes, topic, payload, sources }) => {
                    let [selectedType] = possibleTypes
                    const latestValue = latestValues[topic] ?? null
                    const previousSelectedType = (latestValue && 'selectedType' in latestValue && latestValue.selectedType) || selectedType
                    selectedType = possibleTypes.includes(previousSelectedType) ? previousSelectedType : selectedType

                    /** Update latest values, without overriding the previously selected value */
                    latestValues = { ...latestValues, [topic]: { possibleTypes, payload, selectedType, sources } }

                    /** Updates the tail unless it is stopped */
                    tail = explorer.isTailing ? ExplorerUsecase.createTail({ tail: [{ timestamp, topic, payload, selectedType }, ...tail], tailSize }) : tail
                })

                this.setExplorer({ latestValues, tail })
            })
        )

        /** If dropped, reset the state */
        droppable.onDrop(() => this.setState({ explorer: selectExplorer(initialState) }))

        return () => droppable.drop()
    }
}
